import { FormInputType } from '@doss/prisma/types'
import dayjs from 'dayjs'
import { z } from 'zod'

export const formV2StringDefaultValueSchema = z
  .object({
    value: z.string(),
  })
  .nullable()

export const formV2NumberDefaultValueSchema = z
  .object({
    value: z.number(),
  })
  .nullable()

export const formV2BooleanDefaultValueSchema = z
  .object({
    value: z.boolean(),
  })
  .nullable()

export const formV2StringArrayDefaultValueSchema = z
  .object({
    value: z.array(z.string()),
  })
  .nullable()

const formV2DateDefaultValueSchema = z
  .object({
    value: z.string().refine(
      (val) => {
        if (val === 'now') {
          return true
        }
        return dayjs(val).isValid()
      },
      { message: 'Must be a valid date string or "now"' },
    ),
  })
  .nullable()

// Need to fix types below datetime
export const formV2DefaultValueSchemaMap: Record<FormInputType, z.ZodType> = {
  [FormInputType.SHORT_TEXT]: formV2StringDefaultValueSchema,
  [FormInputType.LONG_TEXT]: formV2StringDefaultValueSchema,
  [FormInputType.EMAIL]: formV2StringDefaultValueSchema,
  [FormInputType.PHONE_NUMBER]: formV2StringDefaultValueSchema,
  [FormInputType.URL]: formV2StringDefaultValueSchema,
  [FormInputType.NUMBER]: formV2NumberDefaultValueSchema,
  [FormInputType.BOOLEAN]: formV2BooleanDefaultValueSchema,
  [FormInputType.DATE]: formV2DateDefaultValueSchema,
  [FormInputType.DATE_TIME]: formV2DateDefaultValueSchema,
  [FormInputType.DROPDOWN]: z
    .union([formV2StringDefaultValueSchema, formV2StringArrayDefaultValueSchema])
    .nullable(),
  [FormInputType.CHECKBOXES]: formV2StringArrayDefaultValueSchema,
  [FormInputType.RADIO_BUTTON]: formV2StringDefaultValueSchema,
  [FormInputType.FILE]: z.null(), // FILE inputs don't support default values
  [FormInputType.SIGNATURE]: z.null(), // SIGNATURE inputs don't support default values
  [FormInputType.LOOKUP]: z
    .union([formV2StringDefaultValueSchema, formV2StringArrayDefaultValueSchema])
    .nullable(),
  [FormInputType.USER]: z.any().nullable(),
  [FormInputType.FORMULA]: z.any().nullable(),
}

/**
 * Validates a default value against its input type schema.
 * Returns true if the default value has the correct structure for its type.
 */
export const isValidFormV2DefaultValue = (
  inputType: FormInputType,
  defaultValue: unknown,
): boolean => {
  const schema = formV2DefaultValueSchemaMap[inputType]
  return schema.safeParse(defaultValue).success
}
